import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { LineupsIcon } from '@sorare/core/src/atoms/icons/LineupsIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BodyL, BodyM, BodyS } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_ENTER } from '@sorare/core/src/constants/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { fantasy } from '@sorare/core/src/lib/glossary';
import {
  desktopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { DiscoverNewCompetition_so5Fixture } from './__generated__/index.graphql';
import discoverBg from './assets/discoverBg.jpg';

const Wrapper = styled(LinkBox)`
  display: flex;
  flex-direction: column;
  border-radius: var(--double-unit);
  overflow: hidden;
  min-width: 200px;

  &.spread {
    min-height: 562px;
  }
  &.inline {
    min-height: 340px;
  }

  flex-basis: 100%;
  padding: 0 var(--intermediate-unit);
  outline: 1px solid var(--c-nd-150);

  @media ${tabletAndAbove} {
    padding: 0;
    min-width: 450px;
    flex-basis: clamp(350px, 100%, 450px);
  }

  @media ${desktopAndAbove} {
    margin-left: var(--triple-unit);
  }
`;

const InnerWrapper = styled(Vertical)`
  flex: 1;
  height: 100%;
  min-height: inherit;

  border-radius: var(--double-unit);
  isolation: isolate;
  position: relative;
  justify-content: space-between;

  background-image: url(${discoverBg});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top center;
  padding: var(--double-unit) 0;

  & > * {
    z-index: 1;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180.74deg,
      rgba(0, 0, 0, 0) 18.9%,
      #000000 89.56%
    );
  }
`;

const CTAsWrapper = styled(Vertical)`
  flex: 1;
`;

const CardCount = styled(BodyS)`
  font-weight: bold;
  padding: 0 var(--intermediate-unit);
  backdrop-filter: blur(10px);
  border-radius: var(--half-unit);
  background-color: var(--c-nd-200);
  color: var(--c-nd-900);
`;

type Props = {
  stacked?: boolean;
  so5Fixture: DiscoverNewCompetition_so5Fixture;
};

export const DiscoverNewCompetition = ({ stacked, so5Fixture }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const { generateSportContextPath } = useSportContext();
  const { currentUser } = useCurrentUserContext();

  const eligibleLeaderboardScarcities = useMemo(() => {
    if (so5Fixture.aasmState !== 'opened') return null;
    const so5LeaderboardGroupsWithoutContenders =
      so5Fixture?.so5LeaderboardGroups.filter(
        so5Leaderboard => !so5Leaderboard.mySo5LeaderboardContenders.length
      ) || [];

    return so5LeaderboardGroupsWithoutContenders
      .map(so5LeaderboardGroup => {
        const rarityType =
          so5LeaderboardGroup.so5LeagueTracks[0]?.entrySo5Leaderboard
            ?.mainRarityType;

        if (
          !rarityType ||
          rarityType === Rarity.common ||
          rarityType === Rarity.custom_series ||
          (currentUser?.cardCounts?.[rarityType] || 0) < 5
        ) {
          return null;
        }
        return rarityType;
      })
      .filter(Boolean);
  }, [
    so5Fixture.aasmState,
    currentUser?.cardCounts,
    so5Fixture?.so5LeaderboardGroups,
  ]);

  if (!isTabletAndAbove) {
    return null;
  }

  const rarityImages = {
    [Rarity.limited]:
      'https://frontend-assets.sorare.com/nba/so5_leaderboard_groups/icon_limited_all_seasons.png',
    [Rarity.rare]:
      'https://frontend-assets.sorare.com/nba/so5_leaderboard_groups/icon_rare_all_seasons.png',
    [Rarity.super_rare]:
      'https://frontend-assets.sorare.com/nba/so5_leaderboard_groups/icon_super_rare_all_seasons.png',
    [Rarity.unique]:
      'https://frontend-assets.sorare.com/nba/so5_leaderboard_groups/icon_unique_all_seasons.png',
  };

  return (
    <Wrapper className={stacked ? 'spread' : 'inline'}>
      <InnerWrapper>
        <CTAsWrapper center gap={2}>
          <LineupsIcon size={4} />
          <BodyL bold>
            <FormattedMessage
              id="DiscoverNewCompetition.discoverNewCompetition"
              defaultMessage="Discover new competitions"
            />
          </BodyL>
          <LinkOverlay
            as={Link}
            to={generateSportContextPath(
              ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_ENTER,
              {
                params: {
                  eventType: so5Fixture.type.toLowerCase(),
                  fixture: so5Fixture.slug,
                },
              }
            )}
          >
            <Button asSpan color="secondary" size="small">
              <FormattedMessage {...fantasy.addTeam} />
            </Button>
          </LinkOverlay>
        </CTAsWrapper>

        {eligibleLeaderboardScarcities?.length && (
          <Vertical center>
            <BodyM>
              <FormattedMessage
                id="DiscoverNewCompetition.eligibleCards"
                defaultMessage="Your eligible cards"
              />
            </BodyM>
            <Horizontal center gap={2}>
              {eligibleLeaderboardScarcities?.map(rarity => (
                <Horizontal key={rarity} center gap={0.5}>
                  <img
                    src={rarityImages[rarity as keyof typeof rarityImages]}
                    alt={rarity}
                    width={24}
                    height={24}
                  />
                  <CardCount>
                    {
                      currentUser?.cardCounts?.[
                        rarity as keyof typeof currentUser.cardCounts
                      ]
                    }
                  </CardCount>
                </Horizontal>
              ))}
            </Horizontal>
          </Vertical>
        )}
      </InnerWrapper>
    </Wrapper>
  );
};

DiscoverNewCompetition.fragments = {
  so5Fixture: gql`
    fragment DiscoverNewCompetition_so5Fixture on So5Fixture {
      slug
      type
      aasmState
      so5LeaderboardGroups {
        slug
        mySo5LeaderboardContenders {
          slug
        }
        eligibleCardsCountByPosition {
          slug
          totalCount
        }
        so5LeagueTracks {
          slug
          entrySo5Leaderboard {
            slug
            mainRarityType
          }
        }
      }
    }
  ` as TypedDocumentNode<DiscoverNewCompetition_so5Fixture>,
};
